import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lock } from "lucide-react";
import { useEffect, useState, memo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButtons } from "@/components/ShareButtons";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useTranslation } from "react-i18next";

interface ArtworkCardProps {
  id: string;
  slug?: string;
  title: string;
  artistName: string;
  price: number;
  imageUrl: string;
  category: string;
  artworkType: "original" | "print" | "print-on-demand";
  stockQuantity?: number;
  showFavorite?: boolean;
  showShare?: boolean;
  minVariantPrice?: number | null;
}

export const ArtworkCard = memo(({
  id,
  slug,
  title,
  artistName,
  price,
  imageUrl,
  category,
  artworkType,
  stockQuantity = 0,
  showFavorite = true,
  showShare = true,
  minVariantPrice,
}: ArtworkCardProps) => {
  const artworkUrl = `/artwork/${slug || id}`;
  const { t } = useTranslation('common');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Use minVariantPrice for print-on-demand, otherwise normal price
  const displayPrice = artworkType === "print-on-demand" && minVariantPrice
    ? minVariantPrice
    : price;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <Link to={artworkUrl} className="h-full block">
      <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer flex flex-col h-full active:scale-[0.98]">
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-muted to-muted/50 flex-shrink-0">
        <OptimizedImage
          src={imageUrl}
          alt={title}
          responsiveSizes={[400, 800]}
          quality={85}
          maxWidth="400px"
          className="transition-all duration-700 group-hover:scale-110 group-hover:brightness-105"
          loading="eager"
          enableBlur={false}
        />
        {(showFavorite || showShare) && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {showFavorite && (
              <div className="bg-background/90 backdrop-blur-md rounded-lg shadow-lg">
                <FavoriteButton artworkId={id} />
              </div>
            )}
            {showShare && (
              <div className="bg-background/90 backdrop-blur-md rounded-lg shadow-lg">
                <ShareButtons 
                  title={`${title} av ${artistName}`}
                  url={artworkUrl}
                  description={t('common.checkOut')}
                />
              </div>
            )}
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1 flex-wrap max-w-[calc(100%-48px)]">
          <Badge variant="outline" className="bg-background/90 text-foreground backdrop-blur-md shadow-lg text-[10px] sm:text-xs">
            {category}
          </Badge>
          {stockQuantity === 0 && (
            <Badge variant="destructive" className="bg-destructive/90 backdrop-blur-md shadow-lg text-[10px] sm:text-xs">
              {t('common.soldOut')}
            </Badge>
          )}
        </div>
      </div>
      <div className="p-3 sm:p-4 flex flex-col flex-1 gap-2 sm:gap-3 bg-gradient-to-b from-card to-card/50">
        {/* Title and Artist - Always same position */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-foreground line-clamp-2 mb-1 leading-tight group-hover:text-primary transition-colors">{title}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground whitespace-normal">{artistName}</p>
          </div>
          <Badge variant="outline" className="ml-2 shrink-0 text-[10px] sm:text-xs border-2">
            {artworkType === "original" ? t('common.original') : t('common.print')}
          </Badge>
        </div>

        {/* Price section - Always same position and height */}
        <div className="mt-auto">
          <div className="flex items-baseline gap-2 mb-1">
            {artworkType === "print-on-demand" ? (
              <p className="font-bold text-base sm:text-lg text-primary">
                {t('common.from')} {displayPrice.toLocaleString("sv-SE")} kr
              </p>
            ) : (
              <p className="font-bold text-base sm:text-lg text-primary">
                {displayPrice.toLocaleString("sv-SE")} kr
              </p>
            )}
          </div>

          {/* Status badge - Always same position and height */}
          <div className="min-h-[24px] flex items-center">
            {stockQuantity === 0 ? (
              <Badge variant="destructive" className="text-[10px] sm:text-xs">
                {t('common.soldOut')}
              </Badge>
            ) : artworkType === "original" && !isLoggedIn ? (
              <Badge variant="secondary" className="flex items-center gap-1 text-[10px] sm:text-xs">
                <Lock className="h-2.5 w-2.5 shrink-0" />
                <span>{t('common.logIn')}</span>
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] sm:text-xs text-muted-foreground">
                {t('common.available')}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Card>
    </Link>
  );
});

ArtworkCard.displayName = 'ArtworkCard';
